// src/components/roles/RoleDeleteModal/RoleDeleteModal.tsx
import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Role } from '../../../types/role.types';
import { ConfirmationModal } from '../../ui/Modal';
import { colors } from '../../../styles/theme/colors';
import { isSystemRole } from '../../../types/role.types';

export interface RoleDeleteModalProps {
  open: boolean;
  roles: Role[];
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading?: boolean;
}

const RoleDeleteModal: React.FC<RoleDeleteModalProps> = ({
  open,
  roles,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Error deleting roles:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Validar que haya roles antes de acceder a sus propiedades
  if (!roles || roles.length === 0) {
    return null;
  }

  // Verificar si hay roles del sistema
  const hasSystemRoles = roles.some((r) => isSystemRole(r));
  const systemRoles = roles.filter((r) => isSystemRole(r));
  const deletableRoles = roles.filter((r) => !isSystemRole(r));

  // Verificar si hay roles con usuarios
  const rolesWithUsers = roles.filter((r) => r.userCount > 0);
  const totalUsersAffected = rolesWithUsers.reduce((sum, r) => sum + r.userCount, 0);

  // Formato diferente según cantidad de roles
  const customContent =
    roles.length === 1 ? (
      // Formato de caja para un solo rol
      <Box
        sx={{
          p: 2.5,
          borderRadius: '8px',
          backgroundColor: colors.grey[50],
          border: `1px solid ${colors.grey[200]}`,
          textAlign: 'left',
          mt: 2,
        }}
      >
        {hasSystemRoles && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Este es un rol del sistema y no puede ser eliminado.
          </Alert>
        )}
        {!hasSystemRoles && rolesWithUsers.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este rol tiene {roles[0].userCount} usuario(s) asignado(s). Se eliminará la asignación
            de todos ellos.
          </Alert>
        )}
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'block',
            mb: 1,
          }}
        >
          Rol
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            mb: 1.5,
          }}
        >
          {roles[0].name}
        </Typography>
        {roles[0].description && (
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              fontSize: '0.8125rem',
            }}
          >
            {roles[0].description}
          </Typography>
        )}
      </Box>
    ) : (
      // Formato de tabla para múltiples roles
      <Box
        sx={{
          mt: 2,
          borderRadius: '8px',
          border: `1px solid ${colors.grey[200]}`,
          overflow: 'hidden',
        }}
      >
        {hasSystemRoles && (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {systemRoles.length} rol(es) del sistema no pueden ser eliminados.
          </Alert>
        )}
        {rolesWithUsers.length > 0 && (
          <Alert severity="warning" sx={{ m: 2, mb: 0, mt: hasSystemRoles ? 1 : 2 }}>
            Se eliminará la asignación de {totalUsersAffected} usuario(s).
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.grey[50] }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Usuarios</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Tipo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell sx={{ fontSize: '0.875rem' }}>{role.name}</TableCell>
                <TableCell sx={{ fontSize: '0.875rem' }}>{role.userCount}</TableCell>
                <TableCell sx={{ fontSize: '0.875rem' }}>
                  {isSystemRole(role) ? (
                    <Typography sx={{ color: colors.error.main, fontWeight: 600 }}>
                      Sistema
                    </Typography>
                  ) : (
                    'Personalizado'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );

  const title =
    roles.length === 1 ? `Eliminar Rol ${roles[0]?.name || ''}` : `Eliminar ${roles.length} Roles`;

  const message =
    roles.length === 1
      ? hasSystemRoles
        ? 'No se puede eliminar un rol del sistema.'
        : `¿Desea eliminar el rol "${roles[0]?.name || ''}"?`
      : hasSystemRoles
        ? `¿Desea eliminar estos ${deletableRoles.length} roles? (${systemRoles.length} roles del sistema no se pueden eliminar)`
        : `¿Desea eliminar estos ${roles.length} roles?`;

  const checkboxLabel =
    roles.length === 1
      ? hasSystemRoles
        ? undefined
        : '¿Desea eliminar este rol?'
      : hasSystemRoles
        ? `¿Desea eliminar estos ${deletableRoles.length} roles?`
        : `¿Desea eliminar estos ${roles.length} roles?`;

  return (
    <ConfirmationModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={title}
      message={message}
      confirmText={isDeleting ? 'Eliminando...' : 'Eliminar'}
      cancelText="Cancelar"
      variant="danger"
      icon={<DeleteIcon />}
      loading={isDeleting || loading}
      requireCheckbox={!hasSystemRoles && checkboxLabel !== undefined}
      checkboxLabel={checkboxLabel}
      customContent={customContent}
      disabled={hasSystemRoles} // Deshabilitar si hay roles del sistema
    />
  );
};

export default RoleDeleteModal;
